import { IamWrapper } from "iam-client";
import React, { useContext, useEffect, useState } from "react";
import PasswordResetNewPasswordForm from "./new-password-form";
import PasswordResetOtpForm from "./otp-form";
import { PasswordResetState } from "./types";
import PasswordResetUsernameForm from "./username-form";

import {
  OnEmailOtpRequiredApi,
  OnIdentifyUsernameRequiredApi,
  OnNewPasswordRequiredApi,
} from "iam-client/dist/wrapper/resetPasswordFlow";
import { UserContext } from "../../context/userContext";
import { ErrorContext } from "../error/provider";
import { LoadingContext } from "../../context/loaderContext";
import Button from "../../ui/widgets/button";
import { useMessageSource } from "react-message-source";
import { IAMExceptionType, IAMExceptionTypeToComponentMap } from "../../types";

import logging from "../../util/logging";
import { useNavigate } from "react-router-dom";
const log = logging("password reset flow");

export default function PasswordReset(): React.JSX.Element {
  const { getMessage } = useMessageSource();

  const navigate = useNavigate();
  const { setError } = useContext(ErrorContext);
  const [exception, setException] = useState<IAMExceptionType>(null);
  const { setOverlayActive } = useContext(LoadingContext);
  const [email, setEmail] = useState<string>();

  const { fetchAuthenticatedUser } = useContext(UserContext);

  const [passwordResetState, setPasswordResetState] =
    useState<PasswordResetState>(PasswordResetState.UsernameForm);

  const [onIdentityUsernameRequired, setOnIdentityUsernameRequired] =
    useState<OnIdentifyUsernameRequiredApi>();

  const [onEmailOtpRequired, setOnEmailOtpRequired] =
    useState<OnEmailOtpRequiredApi>();
  const [onNewPasswordRequired, setOnNewPasswordRequired] =
    useState<OnNewPasswordRequiredApi>();

  const [formError, setFormError] = useState(null);

  function resetFlow() {
    window.location.reload();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onError = (errors: any) => {
    log.error(`On Error ${JSON.stringify(errors)}`);
    const error = errors[0];
    if (error?.code === "EMAIL_OTP_WRONG") {
      setException(IAMExceptionType.TOO_MANY_EMAIL_OTP_ATTEMPTS);
      return;
    }
    setError(errors);
  };

  const resolveGenericErrorHandlers = () => {
    return {
      on5XX: onError,
    };
  };

  useEffect(() => {
    IamWrapper.init({ resolveGenericErrorHandlers });
    const startPasswordReset = async () => {
      IamWrapper.resetPasswordFlow({
        onInit: async ({ start }) => {
          await start();
        },
        onIdentifyUsernameRequired: (params: OnIdentifyUsernameRequiredApi) => {
          log.info("New password flow: On Identity username required");
          setFormError(params.errors);
          setOnIdentityUsernameRequired(params);
        },
        onEmailOtpRequired: (params: OnEmailOtpRequiredApi) => {
          log.info("New password flow: On Email OTP required");
          setFormError(params.errors);
          setOnEmailOtpRequired(params);
          setPasswordResetState(PasswordResetState.OtpForm);
        },
        onNewPasswordRequired: (params: OnNewPasswordRequiredApi) => {
          log.info("New password flow: On new password required");
          setFormError(params.errors);
          setOnNewPasswordRequired(params);
          setPasswordResetState(PasswordResetState.NewPasswordForm);
        },
        onLoadingChange: (loading: boolean) => {
          setOverlayActive(loading);
          log.info("New password flow: Loading " + loading);
        },
        onError,
        onFinish: () => {
          log.info("New password flow: Finished");
          fetchAuthenticatedUser();
          navigate("/login");
        },
      });
    };
    startPasswordReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (exception) {
    const ExceptionComponent = IAMExceptionTypeToComponentMap[exception];
    return (
      <ExceptionComponent
        renderButton={() => {
          return (
            <Button kind="primary" onClick={() => resetFlow()}>
              {getMessage("Error.tooManyOtpAttempts.button")}
            </Button>
          );
        }}
      />
    );
  }

  return (
    <section>
      {passwordResetState === PasswordResetState.UsernameForm && (
        <PasswordResetUsernameForm
          setEmail={setEmail}
          onNext={onIdentityUsernameRequired}
        />
      )}
      {passwordResetState === PasswordResetState.OtpForm && (
        <PasswordResetOtpForm email={email} onNext={onEmailOtpRequired} />
      )}
      {passwordResetState === PasswordResetState.NewPasswordForm && (
        <PasswordResetNewPasswordForm
          onNext={onNewPasswordRequired}
          errors={formError}
          resetFormErrors={() => setFormError(null)}
        />
      )}
    </section>
  );
}
